import React, { useRef, useState, useEffect, useContext } from 'react';
import mapboxgl from 'mapbox-gl';
import { IconButton, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import { FDContext } from 'contexts/Map/fdStore';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { LineGlobeIcon } from 'icons';

mapboxgl.accessToken =
  'pk.eyJ1IjoiZ2lyaXNocGFsbGFnYXR0aSIsImEiOiJja2h6NGVuc20wYndjMnlxaDExbzlsa3B2In0.e94ZfcoPleAA2nqjeX10ag';

let heightOfTheWindow = window.innerHeight;

window.addEventListener('resize', () => {
  heightOfTheWindow = window.innerHeight;
});

const useStyles = makeStyles({
  hoverDisplayContainer: {
    position: 'absolute',
    left: 60,
    top: 0,
    zIndex: 1,
    minHeight: 220,
    paddingLeft: 8,
    minWidth: 160,
    textAlign: 'left',
    color: '#fff',
  },
  shadowGenerator: {
    padding: 30,
    zIndex: 1,
    position: 'relative',
    top: -60,
    boxShadow: '-54px 72px 138px 62px rgba(0,0,0,0.85)',
  },
  hoverContent: {
    position: 'relative',
    zIndex: 2,
  },
  globeIconContainer: {
    position: 'absolute',
    right: 8,
    bottom: 12,
    zIndex: 1,
    height: 48,
    width: 48,
    '@media (max-width: 600px)': {
      bottom: 56,
    },
  },
  globebutton: {
    width: 48,
    height: 48,
  },
  iconRoot: {
    fill: 'none',
  },
  clearButtonContainer: {
    position: 'absolute',
    right: 60,
    bottom: 12,
  },
  clearButton: {
    backgroundColor: '#1D2023',
    fontSize: 14,
  },
  drawerToggleContainer: {
    position: 'absolute',
    right: 8,
    top: '40vh',
    backgroundColor: '#fff',
    borderRadius: 8,
    '@media (max-width: 600px)': {
      display: 'none',
    },
  },
  collapseDrawerButton: {
    backgroundColor: '#000',
    color: '#fff',
    borderRadius: 8,
    padding: 8,
    '&:hover': {
      color: '#999',
    },
  },
  collapseDrawerButtonMobile: {
    backgroundColor: '#000',
    color: '#fff',
    borderRadius: 8,
    padding: 8,
  },
  drawerToggleMobileOpen: {
    position: 'absolute',
    right: 68,
    top: heightOfTheWindow - 340,
    backgroundColor: '#fff',
    borderRadius: 8,
    '@media (min-width: 600px)': {
      display: 'none',
    },
  },
  drawerToggleMobileClosed: {
    position: 'absolute',
    right: 68,
    bottom: 64,
    backgroundColor: '#fff',
    borderRadius: 8,
    '@media (min-width: 600px)': {
      display: 'none',
    },
  },
});

const MapViewForDataFD = (props) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const classes = useStyles();

  const { data, geoJsonData } = props;
  const initialData =
    Array.isArray(data) && data.length > 0 ? data[0].props : {};
  const [zoom, setZoom] = useState(initialData.zoomLevel || 13);
  const [lat, setLat] = useState(initialData.latitude || 50.67); // Set default lat based on your GeoJSON
  const [lng, setLng] = useState(initialData.longitude || 4.79); // Set default lng based on your GeoJSON

  // Use either provided geoJsonData or the initial responseJson
  const [responseData, setResponseData] = useState(
    geoJsonData || initialData.responseJson || null
  );

  const {
    state: { drResult, renderOption },
    dispatch,
  } = useContext(FDContext);

  const [map, setMap] = useState(null);
  const [mapLoad, setMapLoad] = useState(false);
  const [basemap, setBasemap] = useState('satellite-streets-v11');
  const [showClearButton, setShowClearButton] = useState(false);

  // Function to fit map to polygon bounds
  const fitMapToBounds = (geojsonData) => {
    if (!map) return;

    try {
      // Create a bounds object
      const bounds = new mapboxgl.LngLatBounds();

      // Handle both FeatureCollection and single Feature
      if (geojsonData.type === 'FeatureCollection' && geojsonData.features) {
        // Extend the bounds with each feature in a collection
        geojsonData.features.forEach((feature) => {
          processFeature(feature, bounds);
        });
      } else if (geojsonData.type === 'Feature') {
        // Handle a single feature object
        processFeature(geojsonData, bounds);
      }

      // If bounds are valid (not empty), fit map to those bounds
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, {
          padding: 50,
          maxZoom: 18,
        });
      }
    } catch (error) {
      console.error('Error fitting map to bounds:', error);
    }
  };

  // Helper function to process individual features
  const processFeature = (feature, bounds) => {
    if (!feature || !feature.geometry || !feature.geometry.coordinates) return;

    // Handle MultiPolygon
    if (feature.geometry.type === 'MultiPolygon') {
      feature.geometry.coordinates.forEach((polygon) => {
        polygon.forEach((ring) => {
          ring.forEach((coord) => {
            bounds.extend(coord);
          });
        });
      });
    }
    // Handle Polygon (which may have multiple rings for holes)
    else if (feature.geometry.type === 'Polygon') {
      feature.geometry.coordinates.forEach((ring) => {
        ring.forEach((coord) => {
          bounds.extend(coord);
        });
      });
    }
    // Handle other geometry types if needed
    else if (feature.geometry.type === 'Point') {
      bounds.extend(feature.geometry.coordinates);
    } else if (feature.geometry.type === 'LineString') {
      feature.geometry.coordinates.forEach((coord) => {
        bounds.extend(coord);
      });
    }
  };

  useEffect(() => {
    const localMap = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/mapbox/${basemap}`,
      center: [lng, lat],
      zoom,
      hash: true,
      maxZoom: 20,
    });

    localMap.on('load', () => {
      setMapLoad(true);
      localMap.on('zoomend', () => {
        setZoom(localMap.getZoom());
      });

      // Add data layer if responseData exists
      if (
        responseData &&
        responseData.features &&
        responseData.features.length > 0
      ) {
        // Add source
        localMap.addSource('data-layer', {
          type: 'geojson',
          data: responseData,
        });

        // Add fill layer
        localMap.addLayer({
          id: 'data-layer-fill',
          type: 'fill',
          source: 'data-layer',
          paint: {
            'fill-color': '#ce9ed4',
            'fill-opacity': 0.5,
          },
        });

        // Add outline layer
        localMap.addLayer({
          id: 'data-layer-line',
          type: 'line',
          source: 'data-layer',
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
          },
        });

        // Fit map to polygon bounds
        fitMapToBounds(responseData);
      }
    });

    setMap(localMap);
    mapRef.current = localMap;

    return () => {
      localMap.remove();
      setMap(null);
      setMapLoad(false);
      if (dispatch) {
        dispatch({
          type: 'setFDResult',
          payload: {
            fdResult: null,
          },
        });
      }
    };
  }, [basemap, dispatch]);

  // Effect to update source data when responseData changes
  useEffect(() => {
    if (map && mapLoad && responseData) {
      // Check if source already exists
      if (map.getSource('data-layer')) {
        map.getSource('data-layer').setData(responseData);
      } else {
        // Add source if it doesn't exist
        map.addSource('data-layer', {
          type: 'geojson',
          data: responseData,
        });

        // Add fill layer
        map.addLayer({
          id: 'data-layer-fill',
          type: 'fill',
          source: 'data-layer',
          paint: {
            'fill-color': '#ce9ed4',
            'fill-opacity': 0.5,
          },
        });

        // Add outline layer
        map.addLayer({
          id: 'data-layer-line',
          type: 'line',
          source: 'data-layer',
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
          },
        });
      }

      // Fit map to polygon bounds
      fitMapToBounds(responseData);
    }
  }, [responseData, map, mapLoad]);

  const toggleBaseLayer = () => {
    if (basemap === 'satellite-streets-v11') {
      map.setStyle('mapbox://styles/mapbox/light-v11');
      setBasemap('light-v11');
    } else {
      map.setStyle('mapbox://styles/mapbox/satellite-streets-v11');
      setBasemap('satellite-streets-v11');
    }

    // Re-add data sources and layers after style change
    map.once('style.load', () => {
      if (responseData) {
        // Re-add source
        map.addSource('data-layer', {
          type: 'geojson',
          data: responseData,
        });

        // Re-add fill layer
        map.addLayer({
          id: 'data-layer-fill',
          type: 'fill',
          source: 'data-layer',
          paint: {
            'fill-color': '#ce9ed4',
            'fill-opacity': 0.5,
          },
        });

        // Re-add outline layer
        map.addLayer({
          id: 'data-layer-line',
          type: 'line',
          source: 'data-layer',
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
          },
        });
      }
    });
  };

  return (
    <div className="map-and-controls-container">
      <div
        className="map-container"
        ref={mapContainerRef}
        style={{ width: '100%', height: '100vh' }}
      />

      <div className={classes.globeIconContainer}>
        <Paper>
          <IconButton onClick={toggleBaseLayer} className={classes.globebutton}>
            <LineGlobeIcon classes={{ root: classes.iconRoot }} />
          </IconButton>
        </Paper>
      </div>
    </div>
  );
};

export default MapViewForDataFD;
